<!--
  ConversationList — Phase 4-3 / T13.

  Roving-tabindex listbox driven by TanStack Query. Empty / error / loading
  states delegate to T18 components so this file stays composition-only.
-->
<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { conversationsList } from '../lib/api/queries.js';
  import { router } from '../lib/router/route.svelte.js';
  import ConversationListItem from './ConversationListItem.svelte';
  import ConversationsSkeleton from './ConversationsSkeleton.svelte';
  import ConversationsError from './ConversationsError.svelte';
  import EmptyConversations from './EmptyConversations.svelte';

  // G1.7 — optional tagIds filter passed in from ConversationsPage. The
  // query is reactive: switching the filter swaps the cache key and
  // refetches with the new server-side filter applied.
  // G4.3c — optional assignedTo filter ('me' | 'unassigned' | undefined).
  // Forwarded into conversationsList()'s polymorphic filters argument.
  type Props = {
    tagIds?: string[];
    assignedTo?: 'me' | 'unassigned' | undefined;
    /** Which queue. Absent means open — the server's default. */
    status?: 'open' | 'closed';
  };
  let { tagIds = [], assignedTo = undefined, status = 'open' }: Props = $props();

  const q = $derived(
    createQuery(
      conversationsList({
        ...(tagIds.length > 0 ? { tagIds } : {}),
        ...(assignedTo ? { assignedTo } : {}),
        ...(status === 'closed' ? { status } : {}),
      }),
    ),
  );

  let focusedIndex = $state(0);

  const selectedId = $derived(
    router.state.name === 'thread' ? router.state.conversationId : null,
  );

  function activate(id: string) {
    router.navigateToThread(id);
  }

  function handleKey(event: KeyboardEvent) {
    if (!$q.data || $q.data.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusedIndex = Math.min(focusedIndex + 1, $q.data.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusedIndex = Math.max(focusedIndex - 1, 0);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusedIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      focusedIndex = $q.data.length - 1;
    }
  }

  function retry() {
    void $q.refetch();
  }
</script>

{#if $q.isPending}
  <ConversationsSkeleton />
{:else if $q.error}
  <ConversationsError error={$q.error} onRetry={retry} />
{:else if !$q.data || $q.data.length === 0}
  <EmptyConversations />
{:else}
  <div
    class="list"
    role="listbox"
    aria-label="Conversations"
    tabindex="0"
    onkeydown={handleKey}
  >
    {#each $q.data as conv, i (conv.id)}
      <ConversationListItem
        conversation={conv}
        selected={conv.id === selectedId}
        focused={i === focusedIndex}
        onActivate={activate}
      />
    {/each}
  </div>
{/if}

<style>
  .list {
    display: flex;
    flex-direction: column;
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow-y: auto;
    height: 100%;
  }
</style>
